import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { LinearProgress, Box } from "@mui/material";

/**
 * Barra de progreso superior para transiciones de ruta y peticiones
 * - Se activa al cambiar la ruta
 * - También se activa mientras existan fetches pendientes (useFetch incrementa el contador global)
 * - Evita parpadeos mostrando la barra al menos `minVisibleMs` cuando se activa
 */
export default function RouteTransitionLoader() {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const visibleSinceRef = useRef<number | null>(null);
  const minVisibleMs = 300; // evitar parpadeos
  const incrementIntervalRef = useRef<number | null>(null);

  // Comprueba el contador global de fetches pending
  function getPendingFetches(): number {
    return Number(window.__pendingFetchCount || 0);
  }

  useEffect(() => {
    let mounted = true;

    const start = () => {
      if (!mounted) return;
      visibleSinceRef.current = Date.now();
      setActive(true);
      setProgress(30);
      // animar progreso incrementando lentamente
      if (incrementIntervalRef.current == null) {
        incrementIntervalRef.current = window.setInterval(() => {
          setProgress((p) => Math.min(95, p + Math.random() * 8));
        }, 400) as unknown as number;
      }
    };

    const stop = () => {
      const elapsed = visibleSinceRef.current
        ? Date.now() - visibleSinceRef.current
        : 0;
      const remaining = Math.max(0, minVisibleMs - elapsed);
      window.setTimeout(() => {
        setProgress(100);
        // dejar un pequeño delay para mostrar 100%
        window.setTimeout(() => {
          setActive(false);
          setProgress(0);
          if (incrementIntervalRef.current != null) {
            clearInterval(incrementIntervalRef.current);
            incrementIntervalRef.current = null;
          }
        }, 120);
      }, remaining);
    };

    // Iniciar al cambiar la ruta
    start();

    // Escuchar cambios del contador de fetches
    const handler = () => {
      const pending = getPendingFetches();
      if (pending > 0) {
        start();
      } else {
        stop();
      }
    };

    window.addEventListener("pendingFetchesChanged", handler);

    // Si no hay fetches pendientes al montarse, parar después del minVisibleMs
    if (getPendingFetches() === 0) {
      // permitir que Suspense o la carga normal complete
      stop();
    }

    return () => {
      mounted = false;
      window.removeEventListener("pendingFetchesChanged", handler);
      if (incrementIntervalRef.current != null) {
        clearInterval(incrementIntervalRef.current);
        incrementIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // También actualizamos la barra si el contador cambia (por ejemplo: fetch termina)
  useEffect(() => {
    const handler = () => {
      const pending = getPendingFetches();
      if (pending === 0) {
        // permitir que el efecto principal lo cierre
      }
    };
    window.addEventListener("pendingFetchesChanged", handler);
    return () => window.removeEventListener("pendingFetchesChanged", handler);
  }, []);

  if (!active) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        top: 0,
        zIndex: 1400,
        pointerEvents: "none",
      }}
    >
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 3,
          backgroundColor: "transparent",
          "& .MuiLinearProgress-bar": { backgroundColor: "primary.main" },
        }}
      />
    </Box>
  );
}
